// src/components/AddVaccineForm.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Syringe, Calendar } from 'lucide-react';
import './AddVaccineForm.css';

  const VACCINE_TYPES = [
    'BCG',
    'Rota',
    'OPV',
    'DPT-HepB - HIB',
    'PCV-10',
    'FIPV',
    'TCV',
    'Influenza',
    'HPV',
    'Hepatitis A',
    'MR',
    'JE',
    'Meningococcal',
    'pneumococcal',
    'varicella',
    'Other'
  ];

const AddVaccineForm = ({ isOpen, onClose, onAddVaccine, onUpdateVaccine, patient, vaccine }) => {
  const [formData, setFormData] = useState({
    name: '',
    otherName: '',
    dose: '',
    date: new Date().toISOString().split('T')[0],
    status: 'completed'
  });

  React.useEffect(() => {
    if (vaccine) {
      setFormData({
        name: VACCINE_TYPES.includes(vaccine.name) ? vaccine.name : 'Other',
        otherName: VACCINE_TYPES.includes(vaccine.name) ? '' : vaccine.name,
        dose: vaccine.dose || '',
        date: vaccine.date || new Date().toISOString().split('T')[0],
        status: vaccine.status || 'completed'
      });
    } else {
      setFormData({
        name: '',
        otherName: '',
        dose: '',
        date: new Date().toISOString().split('T')[0],
        status: 'completed'
      });
    }
  }, [vaccine, isOpen]);



  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name) {
      // Use otherName if 'Other' is selected, otherwise use the selected name
      const finalName = formData.name === 'Other' ? formData.otherName : formData.name;

      if (formData.name === 'Other' && !finalName) {
        alert('Please specify the vaccine name');
        return;
      }

      if (vaccine) {
        onUpdateVaccine(patient.id, {
          ...formData,
          name: finalName,
          id: vaccine.id
        });
      } else {
        onAddVaccine(patient.id, {
          ...formData,
          name: finalName,
          id: Date.now().toString()
        });
      }
      setFormData({
        name: '',
        otherName: '',
        dose: '',
        date: new Date().toISOString().split('T')[0],
        status: 'completed'
      });
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div
          className="modal-content vaccine-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>{vaccine ? 'Edit Vaccine' : 'Add Vaccine'} for {patient?.name}</h2>
            <motion.button
              className="close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="vaccine-form">
            <div className="form-group">
              <label htmlFor="vaccineName">
                <Syringe size={16} />
                Vaccine Name *
              </label>
              <select
                id="vaccineName"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              >
                <option value="">Select Vaccine</option>
                {VACCINE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {formData.name === 'Other' && (
              <div className="form-group">
                <label htmlFor="otherName">Specify Vaccine Name *</label>
                <input
                  type="text"
                  id="otherName"
                  value={formData.otherName}
                  onChange={(e) => handleChange('otherName', e.target.value)}
                  placeholder="Enter vaccine name"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="dose">Dose Information</label>
              <input
                type="text"
                id="dose"
                value={formData.dose}
                onChange={(e) => handleChange('dose', e.target.value)}
                placeholder="e.g., Dose 1, Booster, Annual"
              />
            </div>

            <div className="form-group">
              <label htmlFor="date">
                <Calendar size={16} />
                Administration Date *
              </label>
              <input
                type="date"
                id="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>

            <div className="form-actions">
              <motion.button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                className="btn-submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Syringe size={18} />
                {vaccine ? 'Update Vaccine' : 'Add Vaccine'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddVaccineForm;